// Photos and screenshots on tickets: kept in the local database and uploaded
// when there is signal (like job photos). The id is made here, so a resend
// after a dropped connection is the same file on the server. A file leaves
// the device only once the server has it.
import { db } from '../../offline/db.js';
import { api, ApiError } from '../../api/client.js';

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());
export const subscribeTicketFiles = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// files: [{ blob, name }]. messageId ties them to the message they came with.
export async function queueTicketFiles(ticketId, messageId, files) {
  if (!files.length) return;
  const at = new Date().toISOString();
  await db.ticketFiles.bulkAdd(files.map((f, i) => ({ id: crypto.randomUUID(), ticketId, messageId, blob: f.blob, name: f.name || `photo-${i + 1}.jpg`, addedAt: at, attempts: 0 })));
  notify();
  flushTicketFiles();
}

export const pendingTicketFiles = (ticketId) => db.ticketFiles.where('ticketId').equals(ticketId).toArray();

let running = null;
let retry = null;
export function flushTicketFiles() {
  if (!running) running = send().finally(() => { running = null; });
  return running;
}

async function send() {
  for (const f of await db.ticketFiles.orderBy('addedAt').toArray()) {
    try {
      await api(`/helpdesk/tickets/${f.ticketId}/files/${f.id}?name=${encodeURIComponent(f.name)}${f.messageId ? `&message=${f.messageId}` : ''}`, { method: 'PUT', raw: f.blob });
      await db.ticketFiles.delete(f.id);
      notify();
    } catch (e) {
      // 409: the ticket itself is still on its way (outbox). Anything else the
      // server says no to will never go: drop it rather than block the queue.
      const later = !(e instanceof ApiError) || e.status === 409 || e.status >= 500 || [401, 408, 429].includes(e.status);
      if (!later || f.attempts > 50) {
        await db.ticketFiles.delete(f.id);
        notify();
        continue;
      }
      clearTimeout(retry);
      retry = setTimeout(flushTicketFiles, Math.min(5 * 60_000, 10_000 * 2 ** Math.min(f.attempts, 5)));
      await db.ticketFiles.update(f.id, { attempts: f.attempts + 1 });
      return;
    }
  }
}
